import React from "react";
import moment from "moment";
import { Navigate, Outlet } from "react-router-dom";

export const Apiurl = () => {
  // return 'http://198.12.253.79:2312/api'
  return "https://attendanceapi.elitewebdemo.com/api";
};

// clear storage
export const RemoveUserSession = () => {
  localStorage.clear();
  sessionStorage.clear();
};

// set forgotemail
export const forgotemail = (forgotemail) => {
  localStorage.setItem("forgotemail", forgotemail);
};

// get forgotemail
export const GetForgotemail = () => {
  return localStorage.getItem("forgotemail") || null;
};

// set token
export const Token = (token, rememberMe = true) => {
  if (rememberMe) {
    localStorage.setItem("token", token);
    sessionStorage.removeItem("token");
  } else {
    sessionStorage.setItem("token", token);
    localStorage.removeItem("token");
  }
};

// get token
export const GetToken = () => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  if (
    !token ||
    token === "undefined" ||
    token === "null" ||
    token.trim() === ""
  ) {
    return null;
  }

  return token;
};

// set Login role
export const SetLoginRole = (value, rememberMe = true) => {
  if (rememberMe) {
    localStorage.setItem("LoginRole", value);
    sessionStorage.removeItem("LoginRole");
  } else {
    sessionStorage.setItem("LoginRole", value);
    localStorage.removeItem("LoginRole");
  }
};

// get login role
export const GetLoginRole = () => {
  return (
    localStorage.getItem("LoginRole") || sessionStorage.getItem("LoginRole")
  );
};

// Calculate working days excluding weekends and holidays
export const calculateWorkingDays = (
  startDate,
  endDate,
  holidays = [],
  leaveFromDuration = "Full Day",
  leaveToDuration = "Full Day"
) => {
  const start = moment(startDate);
  const end = moment(endDate);

  const holidayDates = holidays
    .map((h) => {
      let dateToFormat;
      if (typeof h === "string") {
        dateToFormat = h;
      } else if (h && typeof h === "object" && h.FromDate) {
        dateToFormat = h.FromDate;
      } else if (h && typeof h === "object") {
        dateToFormat = h;
      } else {
        return null;
      }
      const formatted = moment(dateToFormat).format("YYYY-MM-DD");
      return formatted;
    })
    .filter((d) => d !== null && moment(d, "YYYY-MM-DD").isValid());

  let workingDays = 0;
  let currentDate = start.clone();

  const getMultiplier = (duration) => {
    if (duration === "Full Day") return 1;
    if (duration === "Half Day") return 0.5;
    if (duration === "Short Day") return 0.25;
    return 1;
  };

  if (start.isSame(end, "day")) {
    const dateStr = start.format("YYYY-MM-DD");
    const dayOfWeek = start.day();

    const isHoliday = holidayDates.includes(dateStr);
    const isSunday = dayOfWeek === 0;
    const isFirstSaturdayOfMonth = dayOfWeek === 6 && start.date() <= 7;
    const isRegularSaturday = dayOfWeek === 6 && !isFirstSaturdayOfMonth;

    if (isHoliday || isSunday || isRegularSaturday) {
      return 0;
    }

    const multiplier = getMultiplier(leaveFromDuration);
    return multiplier;
  }

  while (currentDate.isSameOrBefore(end)) {
    const dateStr = currentDate.format("YYYY-MM-DD");
    const dayOfWeek = currentDate.day();

    const isHoliday = holidayDates.includes(dateStr);
    const isSunday = dayOfWeek === 0;
    const isFirstSaturdayOfMonth = dayOfWeek === 6 && currentDate.date() <= 7;
    const isRegularSaturday = dayOfWeek === 6 && !isFirstSaturdayOfMonth;

    if (isHoliday || isSunday || isRegularSaturday) {
      currentDate.add(1, "day");
      continue;
    }

    if (currentDate.isSame(start)) {
      const multiplier = getMultiplier(leaveFromDuration);
      workingDays += multiplier;
    } else if (currentDate.isSame(end)) {
      const multiplier = getMultiplier(leaveToDuration);
      workingDays += multiplier;
    } else {
      workingDays += 1;
    }

    currentDate.add(1, "day");
  }

  const result = Math.round(workingDays * 100) / 100;
  return result;
};

// Helper function to get duration enum value
export const getDurationEnumValue = (durationString) => {
  const durationMap = {
    "Full Day": 0,
    "Half Day": 1,
    "Short Day": 2,
  };
  return durationMap[durationString] || 0;
};

// Format NoOfDays to handle decimal values properly
export const formatNoOfDays = (days) => {
  if (!days && days !== 0) return "-";
  const numDays = parseFloat(days);
  if (isNaN(numDays)) return "-";
  return parseFloat(numDays.toFixed(2));
};

export const downloadFile = async (fileUrl, fileName) => {
  try {
    if (!fileUrl) return;

    const response = await fetch(fileUrl);
    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || fileUrl.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

// Download multiple files at once
export const downloadMultipleFiles = (fileUrls) => {
  try {
    if (!fileUrls || fileUrls.length === 0) {
      console.error("No file URLs provided");
      return;
    }

    fileUrls.forEach(({ url, fileName }, index) => {
      setTimeout(() => {
        downloadFile(url, fileName);
      }, index * 500);
    });
  } catch (error) {
    console.error("Error downloading files:", error);
  }
};

// Validate Aadhaar file upload - max 2 files allowed
export const validateAadhaarFiles = (files) => {
  if (!files || files.length === 0) {
    return { isValid: true, error: null };
  }

  if (files.length > 2) {
    return {
      isValid: false,
      error: "Maximum 2 files allowed for Aadhaar upload",
    };
  }

  return { isValid: true, error: null };
};

// Get time-based leave restrictions for same day leave applications
export const getTimeLevelLeaveRestrictions = (selectedDate) => {
  const today = moment().format("YYYY-MM-DD");
  const isToday = moment(selectedDate).format("YYYY-MM-DD") === today;

  if (!isToday) {
    return {
      canApplyFullDay: true,
      canApplyHalfDay: true,
      canApplyShortLeave: true,
      availableHalfDaySlots: [1, 2],
      availableShortLeaveSlots: [1, 2, 3, 4],
      restrictionMessage: null,
    };
  }

  const currentTime = moment();
  const time1130AM = moment().set({ hour: 11, minute: 30, second: 0 });
  const time4PM = moment().set({ hour: 16, minute: 0, second: 0 });

  let restrictions = {
    canApplyFullDay: false,
    canApplyHalfDay: false,
    canApplyShortLeave: false,
    availableHalfDaySlots: [],
    availableShortLeaveSlots: [],
    restrictionMessage: null,
  };

  if (currentTime.isBefore(time1130AM)) {
    restrictions = {
      canApplyFullDay: true,
      canApplyHalfDay: true,
      canApplyShortLeave: true,
      availableHalfDaySlots: [1, 2],
      availableShortLeaveSlots: [1, 2, 3, 4],
      restrictionMessage: null,
    };
  } else if (
    currentTime.isSameOrAfter(time1130AM) &&
    currentTime.isBefore(time4PM)
  ) {
    restrictions = {
      canApplyFullDay: false,
      canApplyHalfDay: true,
      canApplyShortLeave: true,
      availableHalfDaySlots: [2],
      availableShortLeaveSlots: [3, 4],
      restrictionMessage:
        "After 11:30 AM - Only Half Day (Afternoon slot) and Short Leave (last 2 slots) available",
    };
  } else if (currentTime.isSameOrAfter(time4PM)) {
    restrictions = {
      canApplyFullDay: false,
      canApplyHalfDay: false,
      canApplyShortLeave: true,
      availableHalfDaySlots: [],
      availableShortLeaveSlots: [4],
      restrictionMessage:
        "After 4:00 PM - Only Short Leave (4:00 PM - 6:00 PM slot) available",
    };
  }

  return restrictions;
};

export const ProtectedRoute = () => {
  const token = GetToken();

  if (!token) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};